import React, { useMemo, useState, useEffect } from 'react'
import classNames from 'classnames'
import { uniqueId } from 'lodash'
import {
  lockBodyScroll,
  unlockBodyScroll,
} from '../../../shared/utils/lockBodyScroll/lockBodyScroll'
import {
  ModalActions,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTopBar,
} from './components'
import { ModalProps } from './Modal.types'
import css from './Modal.styles.scss'

const Modal: React.FC<ModalProps> = ({
  isOpen,
  handleClose,
  className,
  children,
  showCloseButton = false,
  primaryButton,
  secondaryButton,
  additionalLink,
  icon,
  scrollToTopOnContentChange = false,
  mobileVariant = 'rich',
  subTitle,
  title,
  alignButton,
}) => {
  const showActions = primaryButton || secondaryButton || additionalLink
  const [isVisible, setIsVisible] = useState(false)
  const lockBodyScrollName = useMemo(() => uniqueId('modal-'), [])

  const ModalClasses = classNames(
    css.Modal,
    className,
    !showActions && css['Modal--noActions'],
    mobileVariant === 'basic'
      ? css['Modal__mobile--basic']
      : css['Modal__mobile--rich']
  )

  useEffect(() => {
    // De-couple isVisible state from isOpen to allow for animation
    if (isOpen) {
      setIsVisible(true)
      lockBodyScroll(lockBodyScrollName)
    } else {
      setIsVisible(false)
      unlockBodyScroll(lockBodyScrollName)
    }
    return () => unlockBodyScroll(lockBodyScrollName)
  }, [isOpen, lockBodyScrollName])

  if (!isOpen) return null

  return (
    <>
      <ModalBackdrop handleClose={handleClose} isVisible={isVisible} />
      <div className={ModalClasses}>
        <div className={css.Modal__wrapper}>
          <div className={css.Modal__topBar__header__wrapper}>
            <ModalTopBar
              handleClose={handleClose}
              mobileVariant={mobileVariant}
              showCloseButton={showCloseButton}
            />
            <ModalHeader
              icon={icon}
              mobileVariant={mobileVariant}
              subTitle={subTitle}
              title={title}
            />
          </div>
          <ModalContent scrollToTopOnContentChange={scrollToTopOnContentChange}>
            {children}
          </ModalContent>
          {showActions && (
            <ModalActions
              additionalLink={additionalLink}
              alignButton={alignButton}
              mobileVariant={mobileVariant}
              primaryButton={primaryButton}
              secondaryButton={secondaryButton}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default Modal
